import vueActivity from '@imt/vue-activity/src/store';
import toolbox from '@imt/vue-toolbox/src/store';
import utils, {axiosWithAuth} from '@imt/vue-toolbox/src/utils';
import axios from 'axios';
import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);

export const formatChildren = item => {
    const formatChild = (child, propName) => {
        let regex = new RegExp(`_([a-z0-9])`, 'g'),
            camelName = propName.replace(regex, g => g[1].toUpperCase());
        child[camelName] = child[propName];

        if (regex.test(propName)) {
            delete child[propName];
        }
    };

    Object.keys(item).forEach((propName) => {
        formatChild(item, propName);
    });

    item.children?.forEach(child => {
        Object.keys(child).forEach((propName) => formatChild(child, propName));

        if (child.children) {
            formatChildren(child);
        }
    });

    return item;
};

export const authedAxios = axiosWithAuth();

export const PART_SIZE = 8 * 1024 ** 2;
export const MAX_CONCURRENT_PARTS = 4;

function sliceFilePart(file, partNumber, partSize) {
    const start = (partNumber - 1) * partSize,
        end = partNumber * partSize;

    return file.slice(start, end);
}

async function putFilePart(url, blob, onPartProgress) {
    const response = await axios.put(url, blob, {
            onUploadProgress(event) {
                onPartProgress(event.loaded || 0);
            },
        }),
        etag = response.headers.etag || response.headers.ETag;

    if (!etag) {
        throw new Error('S3 part upload did not return an ETag');
    }

    return etag;
}

function reportUploadProgress(onProgress, bytesSent, fileSize) {
    if (onProgress) {
        onProgress(bytesSent, fileSize);
    }
}

export let getters = {
    getIcon: () => (mimeType) => {
        return {
            'application/msword': 'file-alt',
            'application/vnd.ms-excel': 'file-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'file-excel',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'file-alt',
            'application/pdf': 'file-pdf',
            'audio/mpeg': 'file-audio',
            'video/mp4': 'file-video',
            'video/quicktime': 'file-video',
            'application/mp4': 'file-video',
            'audio/mp4': 'file-video',
            'text/csv': 'file-csv',
            'text/plain': 'file-code',
        }[mimeType] || 'file';
    },
    getValidFolderTypes: () => {
        return [
            'claim',
            'policy',
        ];
    },
    isImage: () => (mimeType) => {
        return ['image/jpeg', 'image/png'].includes(mimeType);
    },
};

export let actions = {
    async abortUpload(_, start) {
        await authedAxios.post(
            `files/upload/${start.s3_id}/abort/`,
            utils.dataFormatter.serialize({
                stuff: {
                    type: 'Upload',
                    upload_id: start.upload_id,
                },
            }),
        );
    },
    async archiveFolder(_, id) {
        await authedAxios.delete(`folders/${id}/`);
    },
    async completeUpload({state}, {file, start, parts}) {
        await authedAxios.post(
            `files/upload/${start.s3_id}/complete/`,
            utils.dataFormatter.serialize({
                stuff: {
                    type: 'File',
                    upload_id: start.upload_id,
                    parts,
                    filename: file.name,
                    folder_id: state.folder.id,
                    mime_type: file.file.type,
                    description: file.description || '',
                },
            }),
        );
    },
    async createFolder(_, folder) {
        const response = await authedAxios.post(`folders/`, utils.dataFormatter.serialize({stuff: {...folder, type: 'Folder'}}));

        return utils.dataFormatter.deserialize(response.data);
    },
    async downloadZip(_, {folder, files}) {
        const response = await authedAxios.post(`download/`, {folder, files}, {
                headers: {'Content-Type': 'application/json'},
                responseType: 'blob',
            }),
            link = document.createElement('a');

        link.href = window.URL.createObjectURL(new Blob([response.data]));
        link.setAttribute('download', 'download.zip');
        document.body.appendChild(link);
        link.click();
    },
    async fetchFiles({commit, state}, {folderId, queryString = '', search = false}) {
        let url = `files/?include=created_by${search ? `&folder=${folderId}` : `&filter[folder_id]=${folderId}`}`;
        url += `&page[offset]=${state[search ? 'searchResults' : 'files'].length}`;
        url += `${search ? `&search=${state.query}` : ''}&${queryString}`;

        const response = await authedAxios.get(url),
            field = search ? 'searchResults' : 'files';

        commit('SET_DATA', {field, data: [
            ...state[field], ...utils.dataFormatter.deserialize(response.data),
        ]});
    },
    async fetchFolders({commit}, {id, queryString = ''}) {
        const response = await authedAxios.get(`folders/${id}/${queryString}`),
            data = {
                folder: formatChildren(response.data.data.folder),
                folders: response.data.data.folders,
                folderTree: formatChildren(response.data.data.tree),
            };

        Object.keys(data).forEach(key => commit('SET_DATA', {field: key, data: data[key]}));
    },
    async logPolicyActivity(_, {data, queryString = ''}) {
        await authedAxios.post(
            `${process.env.VUE_APP_POLICY_DATA_URL}/api/v1/versions/${data.versionId}/activities/${queryString}`,
            utils.dataFormatter.serialize({ stuff: { ...data, type: 'Activity' } })
        );
    },
    async moveFiles(_, {action = 'move', folder, files}) {
        await authedAxios.post(`files/${action}/`, utils.dataFormatter.serialize({stuff: {folder, files, type: 'File'}}));
    },
    async saveFile(_, file) {
        const response = await authedAxios.patch(`files/${file.id}/`, utils.dataFormatter.serialize({stuff: file}));

        return utils.dataFormatter.deserialize(response.data);
    },
    async saveFolder(_, folder) {
        const response = await authedAxios.patch(`folders/${folder.id}/`, utils.dataFormatter.serialize({stuff: folder}));

        return utils.dataFormatter.deserialize(response.data);
    },
    async startUpload(_, file) {
        const response = await authedAxios.post(
                'files/upload/',
                utils.dataFormatter.serialize({
                    stuff: {
                        type: 'Upload',
                        filename: file.name,
                        file_size: file.file.size,
                    },
                }),
            ),
            start = response.data.data;

        if (!start.part_urls) {
            throw new Error('Upload start did not return part URLs');
        }

        return start;
    },
    async uploadPartsToS3(_, {file, partUrls, onProgress, partSize = PART_SIZE}) {
        const uploadedParts = [],
            partBytesLoaded = new Array(partUrls.length).fill(0);
        let nextPartIndex = 0,
            partUploadFailed = false;

        function reportTotalProgress() {
            const bytesSent = partBytesLoaded.reduce((total, loaded) => total + loaded, 0);

            reportUploadProgress(onProgress, bytesSent, file.size);
        }

        async function uploadNextPart() {
            while (!partUploadFailed && nextPartIndex < partUrls.length) {
                const partIndex = nextPartIndex,
                    part = partUrls[partIndex],
                    blob = sliceFilePart(file, part.part_number, partSize);

                nextPartIndex += 1;

                try {
                    const etag = await putFilePart(part.url, blob, (bytesThisPart) => {
                        partBytesLoaded[partIndex] = bytesThisPart;
                        reportTotalProgress();
                    });

                    if (partUploadFailed) {
                        return;
                    }

                    partBytesLoaded[partIndex] = blob.size;
                    reportTotalProgress();
                    uploadedParts.push({
                        part_number: part.part_number,
                        etag,
                    });
                } catch (error) {
                    if (partUploadFailed) {
                        return;
                    }

                    partUploadFailed = true;
                    throw error;
                }
            }
        }

        const workerCount = Math.min(MAX_CONCURRENT_PARTS, partUrls.length);

        await Promise.all(
            Array.from({length: workerCount}, () => uploadNextPart()),
        );

        return uploadedParts.sort((leftPart, rightPart) => leftPart.part_number - rightPart.part_number);
    },
};

export let mutations = {
    REMOVE_FILES(state, fileIds) {
        state.files = state.files.filter(file => !fileIds.includes(file.id));
    },
    SET_DATA(state, {field, data}) {
        state[field] = data;
    },
};

export let state = {
    files: [],
    folder: {},
    folderEditing: null,
    folders: [],
    folderTree: {},
    policyData: {},
    query: null,
    searchResults: [],
};

export default new Vuex.Store({
    modules: {
        toolbox,
        vueActivity,
    },
    state,
    getters,
    mutations,
    actions,
});
